import { FC, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { WholesalerDetailsCard } from './WholesalerDetailsCard'
import { urlAddAdditionalOrderAccounts } from '../../constants/routes'
import { Link } from 'react-router-dom'
import { AdditionalWholesaler } from './types.d'
import { Box, Button } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { Subscriptions } from '@carsys/enums/subscription'
import { BaseStepLayout } from '../BaseStepLayout'
import { StepNavigation } from '../StepNavigation'
import { defaultStepFormProps } from '../types.d'
import { CountryCode } from '@carsys/enums/country-code'
import { WithRunInBackgroundHelper } from '../use-should-run-in-background'

interface SetupOrderAccountsProps {
  additionalWholesalers: AdditionalWholesaler[]
  deleteAdditionalWholesaler: (a: AdditionalWholesaler) => void
  fixedWholesaler: AdditionalWholesaler | null
  onSubmit: () => void
  basePath: string
  modules: Subscriptions[]
  country: string
}

export const SetUpOrderAccountsScreen: FC<defaultStepFormProps & SetupOrderAccountsProps> = ({
  deleteAdditionalWholesaler,
  fixedWholesaler,
  additionalWholesalers,
  basePath,
  prevStep,
  isFirstStep,
  isPersisting,
  onSubmit,
  onSubmitSuccess,
  hideSkip,
  modules,
  country,
  shouldRunInBackground
}) => {
  const { t } = useTranslation()
  const finishStep = useCallback(() => {
    onSubmit()
    onSubmitSuccess()
  }, [onSubmit, onSubmitSuccess])
  /* GMS-15637: this button is hidden on purpose and expected to be re-introduced */
  const tempHideCarCatBE =
    modules.includes(Subscriptions.Module_Portal) && country === CountryCode.BE ? { display: 'none', visibility: 'hidden' } : {}

  const subTitle = fixedWholesaler
    ? t('Auth.Onboarding.set-up-order-accounts-screen-info-content')
    : t('onboarding.order_accounts.order_accounts_info_create_new')

  return (
    <WithRunInBackgroundHelper enabled={shouldRunInBackground} isSubmitting={false} onSubmit={finishStep}>
      <BaseStepLayout title={t('onboarding.order_accounts.order_accounts_header')} subTitles={[subTitle]}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', '& > *': { m: '8px !important' } }}>
          {fixedWholesaler && (
            <WholesalerDetailsCard
              Name={fixedWholesaler.wholesaler?.Name}
              LogoURL={fixedWholesaler.wholesaler?.LogoURL}
              UserId={fixedWholesaler?.Credentials.UserId}
            />
          )}
          {additionalWholesalers.map((cred, id) => (
            <WholesalerDetailsCard
              key={id}
              Name={cred.wholesaler?.Name}
              LogoURL={cred?.wholesaler?.LogoURL}
              UserId={cred?.Credentials?.UserId}
              onDelete={() => deleteAdditionalWholesaler(cred)}
            />
          ))}

          <Box sx={tempHideCarCatBE}>
            <Button variant='outlined' component={Link} to={urlAddAdditionalOrderAccounts(basePath)} startIcon={<AddIcon />}>
              {fixedWholesaler || additionalWholesalers?.length
                ? t('Auth.Onboarding.btnAddOrderAccount')
                : t('Auth.Onboarding.btnAddNewOrderAccount')}
            </Button>
          </Box>
        </Box>
        <StepNavigation prevStep={prevStep} hideBack={isFirstStep} nextStep={finishStep} isLoading={isPersisting} hideSkip={hideSkip} />
      </BaseStepLayout>
    </WithRunInBackgroundHelper>
  )
}
